import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import { showTaxCollectionChangeNotice } from '@/components/taxes/tax-collection-change-notice'
import { getAddressRegionOptions, normalizeAddressRegionCode } from '@/lib/address-region-options'
import { getDefaultHours } from '@/lib/constants/businessHours'
import dayjs from '@/lib/dayjs'
import { Country } from '@/lib/enums/Country'
import { Timezone } from '@/lib/enums/Timezone'
import { useAppForm } from '@/lib/form'
import {
  useCreateLocationMutation,
  useSoftDeleteLocationMutation,
  useUpdateLocationMutation,
} from '@/lib/services/locations/locations.query'
import {
  createLocationSchema,
  updateLocationProfileSchema,
} from '@/lib/services/locations/locations.schema'
import { enumToSelectOptions, getErrorMessage } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { ExternalLink, Mail, MapPin, Phone as PhoneIcon } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import AsideCard from '@/components/patterns/aside-card'
import { GoogleIcon } from '../form/SocialIcons'
import { Button } from '../ui/button'
import { Typography } from '@/components/ui/typography'
import DeleteButton from './DeleteButton'
import SaveBar from './SaveBar'

// Preview component for contact info - mimics storefront display
const ContactPreview = ({ phone, email, phoneCountryCode }) => {
  const { formatPhone } = usePhoneFormatter()

  const hasContact = phone || email

  if (!hasContact) {
    return (
      <div className="text-muted-foreground text-sm italic">No contact information entered yet</div>
    )
  }

  const phoneUrl = phone ? `tel:${phone.replace(/\D/g, '')}` : null
  const emailUrl = email ? `mailto:${email}` : null

  return (
    <div className="space-y-3">
      {phone && (
        <div className="flex items-center gap-2">
          <PhoneIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          <a
            href={phoneUrl}
            className="text-sm text-foreground hover:text-interaction transition-colors"
          >
            {formatPhone(phone, phoneCountryCode)}
          </a>
        </div>
      )}
      {email && (
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
          <a
            href={emailUrl}
            className="text-sm text-foreground hover:text-interaction transition-colors truncate"
          >
            {email}
          </a>
        </div>
      )}
    </div>
  )
}

// Preview component for address info - mimics storefront display
const AddressPreview = ({ address }) => {
  const hasAddress = address?.line1 || address?.city

  if (!hasAddress) {
    return <div className="text-muted-foreground text-sm italic">No address entered yet</div>
  }

  const fullAddress = [
    address?.line1,
    address?.line2,
    address?.city,
    address?.state,
    address?.postal_code,
  ]
    .filter(Boolean)
    .join(', ')
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(fullAddress)}`

  return (
    <div className="space-y-3">
      {address?.name && <div className="text-sm font-medium">{address.name}</div>}
      <div className="text-sm text-muted-foreground space-y-0.5">
        {address?.line1 && <p>{address.line1}</p>}
        {address?.line2 && <p>{address.line2}</p>}
        {(address?.city || address?.state || address?.postal_code) && (
          <p>
            {[address?.city, address?.state].filter(Boolean).join(', ')} {address?.postal_code}
          </p>
        )}
      </div>
      {fullAddress && (
        <Button variant="outline" size="micro" asChild>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3 w-3 mr-1" />
            View on Maps
          </a>
        </Button>
      )}
    </div>
  )
}

const emptyLocation = {
  address: {
    name: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    postal_code_suffix: null,
    country: Country.UnitedStates.Value,
    timezone: dayjs.tz.guess(),
    lat: null,
    lng: null,
  },
  phone: '',
  phone_country_code: Country.UnitedStates.Value,
  email: '',
  google_business_profile_url: '',
  is_active: true,
  is_default: false,
  is_delivery: false,
  delivery_cutoff: 4,
  delivery_hours: getDefaultHours(),
  is_pickup: false,
  pickup_cutoff: 2,
  open_hours: getDefaultHours(),
  is_distance_pricing_enabled: false,
  delivery_max_distance_miles: null,
  delivery_postal_code_fees: [],
  delivery_city_fees: [],
  delivery_distance_fee_ranges: [],
  delivery_area_fees: [],
}

const getLocationProfileAddressPayload = (address = {}) => ({
  name: address.name ?? null,
  line1: address.line1 ?? null,
  line2: address.line2 ?? null,
  city: address.city ?? null,
  state: address.state ?? null,
  postal_code: address.postal_code ?? null,
  postal_code_suffix: address.postal_code_suffix ?? null,
  country: address.country ?? null,
  timezone: address.timezone ?? null,
  lat: address.lat ?? null,
  lng: address.lng ?? null,
  google_place_id: address.google_place_id ?? null,
  is_verified: address.is_verified ?? false,
})

// This form intentionally saves the profile route only. Pickup and delivery settings have
// dedicated settings routes so profile edits cannot accidentally overwrite fulfillment setup.
const getLocationProfilePayload = (value, { canBeDefaultLocation = false } = {}) => ({
  address: getLocationProfileAddressPayload(value.address),
  phone: value.phone,
  phone_country_code: value.phone_country_code,
  email: value.email,
  google_business_profile_url: value.google_business_profile_url,
  is_active: value.is_active,
  is_default: canBeDefaultLocation ? value.is_default : false,
})

const LocationForm = ({ initialData }) => {
  const { show } = usePathHelper().locations
  const [allowAutofill, setAllowAutofill] = useState(false)
  const id = useId()
  const navigate = useNavigate()
  const path = usePathHelper()
  const ability = useAbility()
  const showTaxChanges = (response) =>
    showTaxCollectionChangeNotice(response, {
      canReview: ability.can('list', 'tax-settings'),
      onReview: () => navigate(path.taxes.index().link),
    })

  const transformLocationForForm = (location) => {
    if (!location) {
      return {
        ...emptyLocation,
        address: { ...emptyLocation.address },
        is_default: false,
        delivery_hours: getDefaultHours(),
        open_hours: getDefaultHours(),
        delivery_postal_code_fees: [],
        delivery_city_fees: [],
        delivery_distance_fee_ranges: [],
        delivery_area_fees: [],
      }
    }

    const timezone = dayjs.tz.guess()

    // Convert lat/lng to numbers or null
    // Handle cases where API returns strings or the values might be invalid
    const parseCoordinate = (value) => {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const num = typeof value === 'string' ? parseFloat(value) : Number(value)
      return isNaN(num) ? null : num
    }

    const address = {
      ...location.address,
      state: normalizeAddressRegionCode(location.address?.state),
      timezone: location.address?.timezone || timezone || emptyLocation.address.timezone,
      lat: parseCoordinate(location.address?.lat),
      lng: parseCoordinate(location.address?.lng),
      google_place_id: location.address?.google_place_id,
    }

    const initial = {
      address,
      phone_country_code: location.phone_country_code || Country.UnitedStates.Value,
      phone: location.phone || '',
      email: location.email || '',
      google_business_profile_url: location.google_business_profile_url || '',
      is_active: location.is_active ?? true,
      is_default: location.is_default ?? false,
      is_delivery: location.is_delivery ?? false,
      delivery_cutoff: location.delivery_cutoff ?? 4,
      delivery_hours:
        location.delivery_hours?.length > 0 ? location.delivery_hours : getDefaultHours(),
      is_pickup: location.is_pickup ?? false,
      pickup_cutoff: location.pickup_cutoff ?? 2,
      open_hours: location.open_hours?.length > 0 ? location.open_hours : getDefaultHours(),
      is_distance_pricing_enabled: location.is_distance_pricing_enabled ?? false,
      delivery_max_distance_miles: location.delivery_max_distance_miles ?? null,
      delivery_postal_code_fees: location.delivery_postal_code_fees ?? [],
      delivery_city_fees: location.delivery_city_fees ?? [],
      delivery_distance_fee_ranges: location.delivery_distance_fee_ranges ?? [],
      delivery_area_fees: location.delivery_area_fees ?? [],
    }

    return initial
  }

  const createLocationMutation = useCreateLocationMutation()
  const updateLocationMutation = useUpdateLocationMutation()
  const deleteLocationMutation = useSoftDeleteLocationMutation({
    cleanupOnSuccess: false,
    onSuccess: showTaxChanges,
  })

  const isEditing = !!initialData?.id
  const canBeDefaultLocation = Boolean(initialData?.is_delivery || initialData?.is_pickup)

  const form = useAppForm({
    defaultValues: transformLocationForForm(initialData),
    validators: {
      onChange: isEditing ? updateLocationProfileSchema : createLocationSchema,
      onSubmit: isEditing ? updateLocationProfileSchema : createLocationSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        const mutation = isEditing ? updateLocationMutation : createLocationMutation
        const profilePayload = getLocationProfilePayload(value, { canBeDefaultLocation })
        const data = isEditing ? { locationId: initialData.id, ...profilePayload } : profilePayload
        const verb = isEditing ? 'update' : 'create'

        try {
          const response = await mutation.mutateAsync(data)
          toast.success(`Location ${verb}d successfully`)
          showTaxChanges(response)

          requestAnimationFrame(() => {
            if (isEditing) {
              const updatedLocation = response?.data || response
              if (updatedLocation?.id) {
                formApi.reset(transformLocationForForm(updatedLocation))
                return
              }
            }

            formApi.reset()
          })

          const createdLocationId = response?.location_id || response?.data?.location_id
          if (createdLocationId) {
            return navigate({
              ...show(createdLocationId).link,
              ignoreBlocker: true,
            })
          }
        } catch (error) {
          toast.error(`Failed to ${verb} location`, {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
    onSubmitInvalid: ({ formApi }) => {
      // Show toast notification when validation fails
      if (formApi.state.errors?.length > 0) {
        toast.error('Please fix the errors', {
          description: 'There are validation errors that need to be corrected.',
        })
      }
    },
  })

  const clearPostalCodeSuffix = () => form.setFieldValue('address.postal_code_suffix', null)

  const handlePlaceSelected = (place) => {
    const streetNumber = place.street_number || ''
    const route = place.route || ''
    const line1 = [streetNumber, route].filter(Boolean).join(' ').trim()

    form.setFieldValue('address.line1', line1)
    form.setFieldValue('address.line2', place.subpremise ?? '')
    form.setFieldValue('address.city', place.locality ?? place.neighborhood ?? '')
    form.setFieldValue(
      'address.state',
      normalizeAddressRegionCode(place.administrative_area_level_1)
    )
    form.setFieldValue('address.postal_code', place.postal_code ?? '')
    form.setFieldValue('address.postal_code_suffix', place.postal_code_suffix ?? null)
    form.setFieldValue('address.country', place.country ?? '')
    if (place.timezone) {
      form.setFieldValue('address.timezone', place.timezone ?? '')
    }
    // Save coordinates if available
    if (place.lat !== undefined) {
      form.setFieldValue('address.lat', place.lat)
    }
    if (place.lng !== undefined) {
      form.setFieldValue('address.lng', place.lng)
    }
    if (place.placeId) {
      form.setFieldValue('address.google_place_id', place.placeId)
      form.setFieldValue('address.is_verified', true)
    } else {
      form.setFieldValue('address.google_place_id', null)
      form.setFieldValue('address.is_verified', false)
    }
  }

  const onLocationNameFocus = () => {
    setAllowAutofill(true)
  }

  const onLocationNameBlur = () => {
    setAllowAutofill(false)
  }

  return (
    <div>
      <form.AppForm id={id} aria-label="Location Form">
        <form.Form>
          <div className="flex flex-col gap-4">
            {/* Contact Section */}
            <AsideCard
              title="Contact Information"
              icon={<PhoneIcon className="h-5 w-5" aria-hidden="true" />}
              asideTitle="Customer Preview"
              aside={
                <form.Subscribe
                  selector={(state) => ({
                    phone: state.values.phone,
                    email: state.values.email,
                    phoneCountryCode: state.values.phone_country_code,
                  })}
                >
                  {({ phone, email, phoneCountryCode }) => (
                    <ContactPreview
                      phone={phone}
                      email={email}
                      phoneCountryCode={phoneCountryCode}
                    />
                  )}
                </form.Subscribe>
              }
            >
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 md:col-span-3">
                  <form.AppField name="phone">
                    {(field) => <field.PhoneInput label="Phone" />}
                  </form.AppField>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <form.AppField name="email">
                    {(field) => <field.EmailInput label="Email" />}
                  </form.AppField>
                </div>
              </div>
            </AsideCard>

            {/* Address Section */}
            <AsideCard
              title="Address Information"
              description={
                <Typography variant="supporting">Location address and contact details</Typography>
              }
              icon={<MapPin className="h-5 w-5" aria-hidden="true" />}
              asideTitle="Customer Preview"
              aside={
                <form.Subscribe selector={(state) => state.values.address}>
                  {(address) => <AddressPreview address={address} />}
                </form.Subscribe>
              }
            >
              <div className="grid gap-y-6">
                <form.AppField name="address.name">
                  {(field) => (
                    <field.TextInput
                      id={`${id}-address-name`}
                      label="Name"
                      placeholder="e.g., Main Store, Springfield Store, First Street Store, etc."
                      onFocus={onLocationNameFocus}
                      onBlur={onLocationNameBlur}
                    />
                  )}
                </form.AppField>
                <div className="grid space-y-2">
                  <form.AppField name="address.line1">
                    {(field) => (
                      <field.AddressSearch
                        id={`${id}-address-line1`}
                        label="Address"
                        placeholder="Start typing an address..."
                        onChange={() => {
                          clearPostalCodeSuffix()
                          form.setFieldValue('address.google_place_id', null)
                          form.setFieldValue('address.is_verified', false)
                        }}
                        onAddressSelected={handlePlaceSelected}
                        allowAutofill={allowAutofill}
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="address.line2">
                    {(field) => (
                      <field.TextInput
                        id={`${id}-address-line2`}
                        placeholder="Apartment, Suite, unit, etc. (optional)"
                      />
                    )}
                  </form.AppField>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <form.AppField name="address.city">
                    {(field) => (
                      <field.TextInput
                        id={`${id}-address-city`}
                        label="City"
                        placeholder="City"
                        onChange={clearPostalCodeSuffix}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="address.state">
                    {(field) => (
                      <field.SelectInput
                        id={`${id}-address-state`}
                        onValueChange={clearPostalCodeSuffix}
                        label="State"
                        placeholder="State"
                        options={getAddressRegionOptions(field.state.value)}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="address.postal_code">
                    {(field) => (
                      <field.TextInput
                        id={`${id}-address-postal_code`}
                        onChange={clearPostalCodeSuffix}
                        label="Zip Code"
                        placeholder="Zip Code"
                      />
                    )}
                  </form.AppField>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <form.AppField name="address.country">
                    {(field) => (
                      <field.SelectInput
                        id={`${id}-address-country`}
                        onValueChange={clearPostalCodeSuffix}
                        label="Country"
                        placeholder="Country"
                        options={enumToSelectOptions(Country)}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="address.timezone">
                    {(field) => (
                      <field.SelectInput
                        id={`${id}-address-timezone`}
                        label="Timezone"
                        placeholder="Timezone"
                        options={enumToSelectOptions(Timezone)}
                      />
                    )}
                  </form.AppField>
                </div>

                <div className="flex flex-col gap-y-6">
                  <form.AppField name="is_active">
                    {(field) => <field.SwitchInput id={`${id}-is_active`} label="Active" />}
                  </form.AppField>
                  {canBeDefaultLocation ? (
                    <form.AppField name="is_default">
                      {(field) => <field.SwitchInput id={`${id}-is_default`} label="Default" />}
                    </form.AppField>
                  ) : null}
                </div>
              </div>
            </AsideCard>

            <AsideCard
              title="Google Business Profile"
              description={
                <Typography variant="supporting">
                  Save a Google Maps profile link or Google review request link for this location.
                  Shoppers can be invited to share one more review on Google after leaving feedback.
                </Typography>
              }
              icon={<GoogleIcon className="h-5 w-5 shrink-0" />}
            >
              <form.AppField name="google_business_profile_url">
                {(field) => (
                  <field.TextInput
                    id={`${id}-google-business-profile-url`}
                    label="Google Business Profile URL"
                    placeholder="Paste a Google Maps profile or review request link"
                  />
                )}
              </form.AppField>
            </AsideCard>

            {isEditing && !initialData?.is_default && (
              <Can do="delete" on="locations">
                <DeleteButton
                  className="-mt-6"
                  id={initialData?.id}
                  model="Location"
                  path={path.locations.index()}
                  mutation={deleteLocationMutation}
                  replace
                  onDeleted={deleteLocationMutation.cleanupDeletedLocation}
                />
              </Can>
            )}
          </div>
          <Can do={isEditing ? 'edit' : 'create'} on="locations">
            <div className="mt-6 w-full xl:max-w-4xl">
              <SaveBar submitButtonText={isEditing ? 'Update' : 'Save'} />
            </div>
          </Can>
        </form.Form>
      </form.AppForm>
    </div>
  )
}

export default LocationForm
